import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useAuth as useAuthHook, type UseAuthReturn } from '../hooks/useAuth';
import { setAccessToken, setLogoutFn, setUserContext } from '../lib/apiClient';

const AuthContext = createContext<UseAuthReturn | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const auth = useAuthHook();

  // Sync the access token to apiClient so every request auto-attaches it
  useEffect(() => {
    setAccessToken(auth.accessToken);
  }, [auth.accessToken]);

  // Sync user context so error banners can show plan-aware messaging
  useEffect(() => {
    setUserContext(auth.user ? { userPlan: auth.user.plan, usage: auth.user.usage } : null);
  }, [auth.user?.plan, auth.user?.usage]);

  // Register logout so apiClient's 401 interceptor can trigger it
  useEffect(() => {
    setLogoutFn(() => {
      auth.logout();
    });
  }, [auth.logout]);

  return (
    <AuthContext.Provider value={auth}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Access the shared auth state. Must be used within <AuthProvider>.
 * For backward compatibility, components can still import `useAuth` from hooks/useAuth.ts,
 * but that creates an isolated instance. Prefer `useAuthContext()` for shared state.
 */
export function useAuthContext(): UseAuthReturn {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuthContext must be used within <AuthProvider>');
  }
  return ctx;
}
